import React__default from 'react';
import { OverlayArrow, OverlayTriggerStateContext, Popover as Popover$1, DialogTrigger } from 'react-aria-components';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import { isButtonComponent } from '../utils/isButtonComponent.js';
import './Popover.scss';

const popoverArrow = (React__default.createElement(OverlayArrow, { className: 'popover-arrow--rev' },
    React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '10', height: '6', viewBox: '0 0 10 6', fill: 'none' },
        React__default.createElement("path", { d: 'M4.29308 4.79289L0.000183165 0.5L10.0002 0.5L5.70729 4.79289C5.31677 5.18342 4.6836 5.18342 4.29308 4.79289Z', fill: 'currentColor' }))));
const popoverDefaultProps = {
    'data-rev-component': 'popover',
    offset: 8,
    placement: 'bottom left',
    arrowBoundaryOffset: 5,
};
/**
 * A popover is an overlay element positioned relative to a trigger.
 *
 * ```tsx
 * <PopoverTrigger>
 *   <Button>Open Popover</Button>
 *   <Popover>
 *     <div>Popover content</div>
 *   </Popover>
 * </PopoverTrigger>
 * ```
 */
const Popover = ({ children, showArrow = false, className, padding = 16, rounding = '16', ...props }) => {
    // Adjust offset based on whether arrow is shown
    const offset = showArrow ? 10 : (props.offset ?? popoverDefaultProps.offset);
    const popoverState = React__default.useContext(OverlayTriggerStateContext);
    // Memoize the close callback to prevent unnecessary re-renders of children
    const closePopover = React__default.useCallback(() => popoverState.close(), [popoverState]);
    // Compute content once - avoids recreating a component function on every render
    const popoverContent = typeof children === 'function' ? children(closePopover) : children;
    const spacingAttributes = {
        ...toSpacingAttributes('p', padding),
    };
    return (React__default.createElement(Popover$1, { ...popoverDefaultProps, ...props, offset: offset, className: className, "data-rev-rounding": rounding }, ({ trigger }) => (React__default.createElement(React__default.Fragment, null,
        showArrow &&
            trigger !== 'MenuTrigger' &&
            trigger !== 'SubmenuTrigger' &&
            popoverArrow,
        React__default.createElement("div", { className: 'popover-content--rev', ...spacingAttributes }, popoverContent)))));
};
/**
 * A trigger component that wraps a Button and a Popover.
 *
 * ```tsx
 * <PopoverTrigger>
 *   <Button>Open Popover</Button>
 *   <Popover>
 *     <div>Popover content</div>
 *   </Popover>
 * </PopoverTrigger>
 * ```
 */
const PopoverTrigger = ({ children, isOpen: controlledIsOpen, defaultOpen = false, onOpenChange, triggerOnPressStart = true, }) => {
    // Internal state for uncontrolled mode (only needed when triggerOnPressStart is true)
    const [internalIsOpen, setInternalIsOpen] = React__default.useState(defaultOpen);
    // Use controlled value if provided, otherwise use internal state
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const handleOpenChange = React__default.useCallback((open) => {
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(open);
        }
        onOpenChange?.(open);
    }, [controlledIsOpen, onOpenChange]);
    // Check if the first child is a Button component and enhance it
    const enhancedChildren = React__default.Children.map(children, (child, index) => {
        // Only process the first child (the trigger element)
        if (index === 0 && React__default.isValidElement(child)) {
            if (isButtonComponent(child)) {
                const childProps = child.props;
                if (triggerOnPressStart) {
                    // Add aria-haspopup and use onPressStart to trigger on mousedown
                    return React__default.cloneElement(child, {
                        ...childProps,
                        ariaHaspopup: 'dialog',
                        // Trigger on press start (mousedown) instead of press (mouseup)
                        onPressStart: (e) => {
                            handleOpenChange(!isOpen);
                            childProps.onPressStart?.(e);
                        },
                    });
                }
                else {
                    // Default behavior: just add aria-haspopup, let DialogTrigger handle onPress
                    return React__default.cloneElement(child, {
                        ...childProps,
                        ariaHaspopup: 'dialog',
                    });
                }
            }
        }
        return child;
    });
    // When not using triggerOnPressStart, let DialogTrigger manage state naturally
    if (!triggerOnPressStart) {
        return (React__default.createElement(DialogTrigger, { isOpen: controlledIsOpen, defaultOpen: defaultOpen, onOpenChange: onOpenChange }, enhancedChildren));
    }
    return (React__default.createElement(DialogTrigger, { isOpen: isOpen, onOpenChange: handleOpenChange }, enhancedChildren));
};

export { Popover, PopoverTrigger };
//# sourceMappingURL=Popover.js.map
